/**
 * ⌘K palette: jump to any screen, session or project; run the common actions. Context-aware
 * (navigation-ia.md §3.7): the open session's verbs and the current project's views come first.
 */

import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { shortcutLabel, useShortcutScope } from '@viko/transcript-ui'
import { Search } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { type ActionEntity, actions as entityActions } from '../lib/actions.js'
import { useBackend } from '../lib/backend.js'
import { isEnabled } from '../lib/flags.js'
import { homeHref, PROJECT_VIEWS, projectHref, runHref, SETTINGS_SECTIONS } from '../lib/ia.js' // ia-shell
import { isLiveRun } from '../lib/lanes-model.js'
import { useContextActions } from '../lib/palette-actions.js'
import { askVerbDo } from '../lib/palette-asks.js'
import { askMatches, parsePaletteQuery, runMatches } from '../lib/palette-query.js'
import { shortCommand, verbRows } from '../lib/palette-verbs.js'
import { useProjects, useRuns } from '../lib/queries.js'
import { runMachine } from '../lib/run-machine.js'
import { useSelection } from '../lib/selection-actions.js'
import { applyTheme, nextTheme, storedTheme } from '../lib/theme.js'
import { useAppUi } from './app-ui.js'
import { useDecisionActions } from './decision/DecisionPanel.js'
import { useInboxRows } from './inbox/useInboxRows.js'
import { Keycap } from './inspector/ActionFooter.js'
import { useRouteProject } from './shell/useRouteProject.js' // ia-shell
import { errorText, useToast } from './ui.js'

interface Item {
  id: string
  group: string
  label: string
  /** A muted qualifier after the label ("suggested", "4 need you", the project). */
  hint?: string
  /** A verb: its keycap and the matching `viko` command (shown in the footer when highlighted). */
  key?: string
  /** A navigation shortcut, shown as a keycap (not a verb). */
  keys?: string
  command?: string | null
  danger?: boolean
  disabledReason?: string
  /** Extra words for the fuzzy match (not shown). */
  keywords?: string
  run: () => void
}

export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const nav = useNavigate()
  const ui = useAppUi()
  const runs = useRuns()
  const projects = useProjects()
  const [q, setQ] = useState('')
  const [sel, setSel] = useState(0)
  const input = useRef<HTMLInputElement>(null)
  useShortcutScope('dialog:palette', open) // keyboard-shortcuts: the palette owns the keyboard
  const b = useBackend()
  const toast = useToast()
  const selection = useSelection()
  const inbox = useInboxRows()
  const decide = useDecisionActions()
  // ui-p0: verbs for what is on screen (the open session registers Land, Allow, Stop, Copy…).
  const context = useContextActions()
  // ia-shell: context-aware — the current tab's project first; canonical URLs, no redirect hops.
  const tabbed = useMemo(() => isEnabled('lanesTab'), [])
  const here = useRouteProject()
  const hereName = projects.data?.find((p) => p.id === here)?.name
  const [term, setTerm] = useState('')
  // Cross-transcript search (FTS on the hub), debounced.
  useEffect(() => {
    const t = setTimeout(() => setTerm(q.trim()), 220)
    return () => clearTimeout(t)
  }, [q])
  const hits = useQuery({
    queryKey: ['search', term],
    queryFn: () => b.api.search(term),
    enabled: open && term.length >= 3,
    staleTime: 30_000,
    retry: false,
  })

  useEffect(() => {
    if (!open) return
    setQ('')
    setSel(0)
    const prev = document.activeElement as HTMLElement | null
    queueMicrotask(() => input.current?.focus())
    return () => {
      // Back to where the palette was opened from, unless an action moved focus on purpose.
      requestAnimationFrame(() => {
        const at = document.activeElement
        if ((!at || at === document.body) && prev?.isConnected && prev !== document.body)
          prev.focus()
      })
    }
  }, [open])

  // Verbs (optimal decision 6): the selection's, then every open ask's, then Stop. All built from
  // the shared registry, so a key or command shown here is the one the footer and CLI use.
  const latest = useRef({ decide, toast, nav })
  latest.current = { decide, toast, nav }
  const askVerbs = useMemo(
    () =>
      inbox.rows
        .filter((r) => r.kind === 'decision' && r.view?.open && r.view.options.length)
        .slice(0, 12)
        .map((r) => {
          const v = r.view as NonNullable<typeof r.view>
          const entity: ActionEntity = {
            kind: 'decision',
            id: v.id,
            title: r.title,
            ...(v.runId ? { runId: v.runId } : {}),
            options: v.options.slice(0, 9).map((o) => ({
              n: o.key,
              label: o.label,
              ...(o.recommended ? { suggested: true } : {}),
            })),
          }
          const open = () => void latest.current.nav({ href: homeHref({ v: 'needs', i: r.key }) })
          const send = async (
            a: Parameters<typeof latest.current.decide.answer>[1],
            ok: string,
          ) => {
            try {
              if (await latest.current.decide.answer(v, a)) latest.current.toast(ok)
            } catch (e) {
              latest.current.toast(errorText(e), 'error')
            }
          }
          return {
            key: r.key,
            title: r.title,
            actions: entityActions(entity, {
              do: (verb) =>
                askVerbDo<Parameters<typeof send>[0]>(verb, v, {
                  send: (a, ok) => void send(a, ok),
                  open,
                }),
            }),
          }
        }),
    [inbox.rows],
  )
  const stops = useMemo(
    () =>
      (runs.data ?? [])
        .filter(isLiveRun)
        .slice(0, 8)
        .map((r) => ({
          title: r.title ?? r.runId,
          run: () =>
            void (async () => {
              try {
                const machine = await runMachine(r.machineId, r.runId, () => b.api.runs())
                await b.dispatch('run.interrupt', machine, { runId: r.runId, cancelQueued: true })
                latest.current.toast('Stopped')
              } catch (e) {
                latest.current.toast(errorText(e), 'error')
              }
            })(),
        })),
    [runs.data, b],
  )
  // "suggested" after the answer an agent recommends (the selection's or any open ask's).
  const suggested = useMemo(() => {
    const m = new Map<string, string>()
    for (const r of inbox.rows) {
      const o = r.view?.options.find((x) => x.recommended)
      if (o) m.set(r.key, `Pick ${o.label}`)
    }
    return m
  }, [inbox.rows])
  const verbItems = useMemo<Item[]>(
    () =>
      verbRows({ selection, asks: askVerbs, stops }).map((v) => ({
        id: `verb:${v.id}`,
        group: v.group,
        label: v.label,
        ...(suggested.get(v.owner === 'sel' ? (selection?.key ?? '') : v.owner) === v.label
          ? { hint: 'suggested' }
          : {}),
        key: v.key,
        command: v.command,
        ...(v.danger ? { danger: true } : {}),
        ...(v.disabledReason ? { disabledReason: v.disabledReason } : {}),
        keywords: `${v.command ?? ''} ${v.group}`,
        run: v.run,
      })),
    [selection, askVerbs, stops, suggested],
  )

  const items = useMemo<Item[]>(() => {
    const go = (to: string, params?: Record<string, string>) => () =>
      void nav({ to, ...(params ? { params } : {}) } as never)
    const href = (to: string) => () => void nav({ href: to })
    const base: Item[] = [
      ...verbItems,
      ...context.map((a) => ({
        id: `ctx:${a.id}`,
        group: 'This session',
        label: a.label,
        ...(a.hint ? { hint: a.hint } : {}),
        ...(a.keywords ? { keywords: a.keywords } : {}),
        run: a.run,
      })),
      // ---- ia-shell: the current project's views first, then Home's views and Settings ----
      ...(tabbed
        ? [
            ...(here
              ? PROJECT_VIEWS.map((v) => ({
                  id: `pv:${v.id || 'lanes'}`,
                  group: hereName ?? 'This project',
                  label: `${hereName ?? 'Project'} › ${v.label}`,
                  keys: v.keys,
                  run: href(projectHref(here, v.id)),
                }))
              : []),
            {
              id: 'home',
              group: 'Go to',
              label: 'Home',
              keys: 'g 0',
              keywords: 'everything tab 0',
              run: href('/home'),
            },
            {
              id: 'i',
              group: 'Go to',
              label: 'Needs you',
              keys: shortcutLabel('global.go.inbox'),
              keywords: 'inbox decisions asks',
              run: href(homeHref({ v: 'needs' })),
            },
            {
              id: 'n',
              group: 'Go to',
              label: 'Running',
              keys: shortcutLabel('global.go.now'),
              keywords: 'now agents sessions',
              run: href(homeHref({ v: 'running' })),
            },
            {
              id: 'o',
              group: 'Go to',
              label: 'Projects',
              keys: shortcutLabel('global.go.overview'),
              keywords: 'overview all projects glance ideas',
              run: href(homeHref({ v: 'projects' })),
            },
            ...SETTINGS_SECTIONS.map((x) => ({
              id: `set:${x.id}`,
              group: 'Settings',
              label: `Settings › ${x.label}`,
              hint: x.blurb,
              run: href(`/settings/${x.id}`),
            })),
            {
              id: 'voice',
              group: 'Go to',
              label: 'Voice mode',
              run: href('/voice'),
            },
          ]
        : [
            {
              id: 'n',
              group: 'Go to',
              label: 'Now',
              keys: shortcutLabel('global.go.now'),
              run: go('/now'),
            },
            {
              id: 'i',
              group: 'Go to',
              label: 'Inbox',
              keys: shortcutLabel('global.go.inbox'),
              run: go('/inbox'),
            },
            {
              id: 'o',
              group: 'Go to',
              label: 'Overview',
              keys: shortcutLabel('global.go.overview'),
              keywords: 'all projects glance',
              run: go('/overview'),
            },
            {
              id: 'p',
              group: 'Go to',
              label: 'Projects',
              keys: shortcutLabel('global.go.projects'),
              run: go(isEnabled('lanesTab') ? '/p' : '/projects'),
            },
            {
              id: 'rules',
              group: 'Go to',
              label: 'Rules & memory',
              run: go('/rules'),
            },
            // ---- g8-voice ----
            {
              id: 'voice',
              group: 'Go to',
              label: 'Voice mode',
              run: go('/voice'),
            },
            // ---- end g8-voice ----
            {
              id: 'rel',
              group: 'Go to',
              label: 'Releases & builds',
              run: go('/releases'),
            },
            {
              id: 'y',
              group: 'Go to',
              label: 'Machines, devices & settings',
              keys: shortcutLabel('global.go.you'),
              run: go('/settings'),
            },
          ]),
      // ---- end ia-shell ----
      {
        id: 'cap',
        group: 'Actions',
        label: 'Capture an idea',
        keys: shortcutLabel('global.capture'),
        run: () => ui.openCapture(),
      },
      {
        id: 'start',
        group: 'Actions',
        label: 'Start a session',
        keys: shortcutLabel('global.newSession'),
        run: () => ui.openStart(),
      },
      {
        id: 'keys',
        group: 'Actions',
        label: 'Keyboard shortcuts',
        keys: shortcutLabel('global.help'),
        run: () => ui.openShortcuts?.(),
      },
      {
        id: 'enroll',
        group: 'Actions',
        label: 'Add a machine',
        run: go('/settings/enroll'),
      },
      {
        id: 'theme',
        group: 'Actions',
        label: 'Cycle theme (dark → light → match system)',
        run: () => {
          applyTheme(nextTheme(storedTheme()))
        },
      },
    ]
    for (const r of runs.data ?? [])
      base.push({
        id: `run:${r.runId}`,
        group: 'Sessions',
        label: r.title ?? r.runId,
        hint: r.projectName,
        run: href(runHref(r.runId, r.projectId)),
      })
    for (const p of projects.data ?? [])
      base.push({
        id: `p:${p.id}`,
        group: 'Projects',
        label: p.name,
        hint: p.repo,
        run: href(projectHref(p.id)),
      })
    for (const p of projects.data ?? [])
      for (const w of p.workstreams)
        base.push({
          id: `w:${w.id}`,
          group: 'Workstreams',
          label: w.name,
          hint: p.name,
          run: href(projectHref(p.id, '', `ws:${w.id}`)),
        })
    return base
  }, [runs.data, projects.data, nav, ui, context, tabbed, here, hereName, verbItems])

  const titleOf = useMemo(
    () => new Map((runs.data ?? []).map((r) => [r.runId, r.title ?? r.runId])),
    [runs.data],
  )

  const parsed = useMemo(() => parsePaletteQuery(q), [q])
  const filtered = useMemo(() => {
    // p: a: since: is: narrow sessions and open asks; the rest of the text is a fuzzy match.
    if (parsed.active) {
      const now = Date.now()
      const t = parsed.text.toLowerCase()
      const runsOut: Item[] = (runs.data ?? [])
        .filter((r) => runMatches(r, parsed.filters, now))
        .filter(
          (r) => !t || fuzzy(`${r.title ?? r.runId} ${r.projectName ?? ''}`.toLowerCase(), t) > 0,
        )
        .sort((a, b) => (b.updatedAt ?? 0) - (a.updatedAt ?? 0))
        .slice(0, 30)
        .map((r) => ({
          id: `run:${r.runId}`,
          group: 'Sessions',
          label: r.title ?? r.runId,
          hint: [r.projectName, String(r.status)].filter(Boolean).join(' · '),
          run: () => void nav({ href: runHref(r.runId, r.projectId) }),
        }))
      const asksOut: Item[] = inbox.rows
        .filter((r) => askMatches({ ...r, agentLabel: r.agentLabel }, parsed.filters, now))
        .filter((r) => !t || fuzzy(`${r.title} ${r.projectName ?? ''}`.toLowerCase(), t) > 0)
        .slice(0, 15)
        .map((r) => ({
          id: `ask:${r.key}`,
          group: 'Needs you',
          label: r.title,
          ...(r.projectName ? { hint: r.projectName } : {}),
          run: () => void nav({ href: homeHref({ v: 'needs', i: r.key }) }),
        }))
      // `is:needs` is the asks; any other state is about sessions.
      return parsed.filters.is && !['needs', 'waiting'].includes(parsed.filters.is.toLowerCase())
        ? runsOut
        : [...asksOut, ...runsOut]
    }
    const s = q.trim().toLowerCase()
    if (!s) return items.filter((i) => i.group !== 'Sessions' || items.indexOf(i) < 20)
    return items
      .map((i) => ({
        i,
        score: fuzzy(
          `${i.label} ${i.hint ?? ''} ${i.keywords ?? ''} ${i.key ?? ''}`.toLowerCase(),
          s,
        ),
      }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score)
      .map((x) => x.i)
      .slice(0, 30)
      .concat(
        (term.length >= 3 && term === q.trim() ? (hits.data ?? []) : []).slice(0, 15).map((h) => {
          const runId = h.runId ?? h.run_id
          return {
            id: `hit:${runId}:${h.seq}`,
            group: 'In transcripts',
            label: h.snippet.replace(/[[\]]/g, '').replace(/\s+/g, ' '),
            hint: `${h.title ?? titleOf.get(runId) ?? runId}${h.source && h.source !== 'viko' ? ` · ${h.source}` : ''}`,
            run: () =>
              void nav({
                href: runHref(runId, runs.data?.find((r) => r.runId === runId)?.projectId, h.seq),
              }),
          }
        }),
      )
  }, [q, parsed, items, term, hits.data, titleOf, nav, runs.data, inbox.rows])

  if (!open) return null
  const choose = (i: Item | undefined) => {
    if (!i || i.disabledReason) return
    onClose()
    i.run()
  }
  const current = filtered[sel]
  const footerCmd = current?.command ?? null
  const copy = (full: string) =>
    void navigator.clipboard
      ?.writeText(full)
      .then(() => toast('Command copied'))
      .catch(() => toast('Copy failed', 'error'))
  let lastGroup = ''
  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: backdrop click closes
    // biome-ignore lint/a11y/useKeyWithClickEvents: Esc handled on the input
    <div className="palette" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="palette__panel" role="dialog" aria-modal="true" aria-label="Command palette">
        <div className="palette__bar">
          <Search size={16} className="palette__glass" aria-hidden="true" />
          <input
            ref={input}
            className="palette__input"
            placeholder="Search, act, or filter: p: since: is:"
            value={q}
            role="combobox"
            aria-expanded="true"
            aria-controls="palette-list"
            aria-activedescendant={current ? `pal-${current.id}` : undefined}
            onChange={(e) => {
              setQ(e.target.value)
              setSel(0)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose()
              else if (e.key === 'ArrowDown') {
                e.preventDefault()
                setSel((s) => Math.min(filtered.length - 1, s + 1))
              } else if (e.key === 'ArrowUp') {
                e.preventDefault()
                setSel((s) => Math.max(0, s - 1))
              } else if (e.key === 'Enter') {
                e.preventDefault()
                choose(current)
              } else if (
                (e.metaKey || e.ctrlKey) &&
                e.key.toLowerCase() === 'c' &&
                footerCmd &&
                e.currentTarget.selectionStart === e.currentTarget.selectionEnd
              ) {
                // ⌘C with no text selected copies the highlighted entry's full `viko` command.
                e.preventDefault()
                copy(footerCmd)
              }
            }}
          />
        </div>
        <div className="palette__list" id="palette-list" role="listbox" aria-label="Results">
          {filtered.length === 0 ? (
            <div className="palette__group">
              {hits.isFetching ? 'Searching transcripts…' : 'No matches'}
            </div>
          ) : null}
          {filtered.map((i, idx) => {
            const header = i.group !== lastGroup ? i.group : null
            lastGroup = i.group
            return (
              <div key={i.id}>
                {header ? <div className="palette__group">{header}</div> : null}
                <button
                  id={`pal-${i.id}`}
                  type="button"
                  role="option"
                  aria-selected={idx === sel}
                  className="palette__item"
                  onMouseEnter={() => setSel(idx)}
                  onClick={() => choose(i)}
                  data-verb={i.key ? '' : undefined}
                  {...(i.disabledReason ? { 'aria-disabled': true, title: i.disabledReason } : {})}
                >
                  <span className={`palette__label${i.danger ? ' is-danger' : ''}`}>{i.label}</span>
                  {i.disabledReason ? (
                    <span className="palette__hint">{i.disabledReason}</span>
                  ) : i.hint ? (
                    <span className="palette__hint">{i.hint}</span>
                  ) : null}
                  {i.key || i.keys ? <Keycap>{i.key ?? i.keys ?? ''}</Keycap> : null}
                </button>
              </div>
            )
          })}
        </div>
        <div className="palette__foot" data-testid="palette-footer">
          {footerCmd ? (
            <>
              <code className="palette__cmd" title={footerCmd}>
                {shortCommand(footerCmd)}
              </code>
              <button type="button" className="palette__copy" onClick={() => copy(footerCmd)}>
                <Keycap>⌘C</Keycap> copy command
              </button>
            </>
          ) : (
            <span className="palette__cmd is-empty">
              {current ? '' : 'Type to search sessions, projects and transcripts'}
            </span>
          )}
          <span className="palette__run">
            <Keycap>⏎</Keycap> {current?.key ? 'run' : 'open'}
          </span>
        </div>
      </div>
    </div>
  )
}

/** Subsequence match; contiguous and word-start hits score higher. */
export function fuzzy(hay: string, needle: string): number {
  if (hay.includes(needle)) return 100 - hay.indexOf(needle)
  let score = 0
  let j = 0
  for (let i = 0; i < hay.length && j < needle.length; i++) {
    if (hay[i] === needle[j]) {
      score += i === 0 || hay[i - 1] === ' ' ? 3 : 1
      j++
    }
  }
  return j === needle.length ? score : 0
}
